import React, { useState, useMemo, useEffect, useRef } from 'react';

import { ContentHeader } from 'components/ContentHeader/ContentHeader';
import Sharing from 'components/Sharing';
import { Redirect } from 'react-router-dom';
import useAppStorage from 'hooks/useAppStorage';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { postcardApiUrl } from 'settings';
import { postcardTemplates } from 'config/postcardTemplates';
import clsx from 'clsx';

const quillModules = {
  toolbar: false,
};

/** Default API path when postcardApiUrl is not set (Laravel on same domain) */
const DEFAULT_POSTCARD_API_PATH = '/api/postcard/generate';
const appURL = import.meta.env.VITE_APP_URL;

// APP ID's that are allowed to access the template view. Currently, only Thai the Knot
const PERMISSABLE_APP_IDS = new Set(['617']);

const TemplateView = () => {
  const { user } = useAppStorage();
  const userData = user?.response || user;
  const currentAppId = String(import.meta.env.VITE_APP_ID || '');
  const shouldRedirect = !PERMISSABLE_APP_IDS.has(currentAppId);

  const [items] = useState(() => {
    return postcardTemplates;
  });
  const firstItem = useMemo(() => {
    return items[0];
  }, [items]);
  const [selected, setSelected] = useState(firstItem?.id ?? null);
  const [templateURL, setTemplateURL] = useState(firstItem?.url ?? null);
  const [messageHtml, setMessageHtml] = useState('');
  const [contactHtml, setContactHtml] = useState('');
  const [generating, setGenerating] = useState(false);
  const [generateError, setGenerateError] = useState(null);

  const selectedItem = items.find((item) => {
    return item.id === selected;
  });

  const params = new URLSearchParams(window.location.search);
  const isMobile = params.get('mobile') === 'true';

  const defaultContactHtml = useMemo(() => {
    const name = [userData?.first_name, userData?.last_name].filter(Boolean).join(' ');
    const line2 = [userData?.agency_name, userData?.title].filter(Boolean).join(' ');
    const email = userData?.email || '';
    const parts = [name, line2, email].filter(Boolean);
    return parts
      .map((p) => {
        return `<p>${p}</p>`;
      })
      .join('');
  }, [userData?.first_name, userData?.last_name, userData?.agency_name, userData?.title, userData?.email]);

  const contactPrefilledRef = useRef(false);
  useEffect(() => {
    if (defaultContactHtml && !contactPrefilledRef.current) {
      contactPrefilledRef.current = true;
      setContactHtml(defaultContactHtml);
    }
  }, [defaultContactHtml]);

  const loadTemplate = (itemId) => {
    const item = items.find((i) => {
      return i.id === itemId;
    });
    if (item) {
      setTemplateURL(item.url);
      setSelected(itemId);
      setGenerateError(null);
    }
  };

  const clearText = () => {
    setMessageHtml('');
    setContactHtml('');
    setTemplateURL(selectedItem?.url ?? firstItem?.url ?? null);
    setGenerateError(null);
  };

  const getPostcardApiUrl = () => {
    const base = typeof postcardApiUrl === 'string' && postcardApiUrl ? postcardApiUrl : '';
    if (base) return base.replace(/\/$/, '');
    const origin = typeof window !== 'undefined' && window.location?.origin ? window.location.origin : appURL;
    return `${origin.replace(/\/$/, '')}${DEFAULT_POSTCARD_API_PATH}`;
  };

  const submitText = async () => {
    if (!selectedItem) return;
    const messageHtmlToSend = messageHtml?.trim() || '';
    const contactHtmlToSend = (contactHtml || defaultContactHtml)?.trim() || '';
    setGenerating(true);
    setGenerateError(null);
    try {
      const url = getPostcardApiUrl();
      const res = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          template_id: selectedItem.template_id,
          filename: selectedItem.filename,
          template_url: selectedItem.url,
          message_html: messageHtmlToSend,
          contact_html: contactHtmlToSend,
        }),
      });
      const data = await res.json().catch(() => {
        return {};
      });
      if (!res.ok) {
        throw new Error(data.message || data.error || `Request failed (${res.status})`);
      }
      const imageUrl = data.url ?? data.image_url ?? data.imageUrl;
      if (imageUrl) {
        setTemplateURL(imageUrl);
      } else {
        throw new Error('No image URL in response');
      }
    } catch (err) {
      setGenerateError(err.message || 'Failed to generate postcard');
    } finally {
      setGenerating(false);
    }
  };

  if (shouldRedirect) {
    return <Redirect to="/main" />;
  }

  return (
    <div
      className={clsx('contentContainer', {
        'contentContainer--mobile': isMobile,
      })}
    >
      {window.location.pathname.includes('templates-mobile') && <style>{'.cookieConsent { display: none; }'}</style>}
      <ContentHeader header="Templates" pageHeader />
      <div className="mainGrid">
        <div className="leftSide">
          <div className="selectedCard">
            <div>
              {templateURL && (
                <a data-fancybox={templateURL} data-src={templateURL} href={templateURL} key={templateURL}>
                  <img src={templateURL} alt={selectedItem?.title || 'template'} />
                </a>
              )}
            </div>
          </div>
        </div>
        <div className="rightSide">
          <div className="templateView__templates">
            <h6>{'Step 1: Choose a Template'}</h6>
            <div className="templateView__templateGrid">
              {items.map((item) => {
                return (
                  <button
                    key={item.id}
                    type="button"
                    className={clsx('templateView__templateCard', {
                      'templateView__templateCard--selected': selected === item.id,
                    })}
                    onClick={() => {
                      return loadTemplate(item.id);
                    }}
                    disabled={generating}
                  >
                    <span className="templateView__templateThumb">
                      <img src={item.thumbnail} alt="" />
                    </span>
                    <span className="templateView__templateTitle">{item.title}</span>
                  </button>
                );
              })}
            </div>
          </div>
          <div>
            <h6>{'Step 2: Type your personalised text'}</h6>
            <form>
              <label className="templateView__label">{'Your message'}</label>
              <ReactQuill
                theme="snow"
                value={messageHtml}
                onChange={setMessageHtml}
                placeholder="Your personal message here..."
                modules={quillModules}
                className="templateView__editor templateView__editor--message"
                readOnly={generating}
              />
              <label className="templateView__label">{'Contact information'}</label>
              <ReactQuill
                theme="snow"
                value={contactHtml}
                onChange={setContactHtml}
                placeholder="Your contact information..."
                modules={quillModules}
                className="templateView__editor templateView__editor--contact"
                readOnly={generating}
              />
              {generateError && (
                <p className="templateView__error" role="alert">
                  {generateError}
                </p>
              )}
              <div className="buttonGridForm">
                <button type="button" id="clear" onClick={clearText} disabled={generating}>
                  {'Clear'}
                </button>
                <button type="button" id="submit" onClick={submitText} disabled={generating}>
                  {generating ? 'Generating…' : 'Submit'}
                </button>
              </div>
            </form>
          </div>
          <div>
            <h6>{'Step 3: Share your card'}</h6>
            {selectedItem && templateURL ? (
              <div className="templateView__shareRow">
                <Sharing url={templateURL} />
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TemplateView;
